export const LAWN_PLAN_ENGINE_VERSION = '2026-06-06-deterministic-v1'

export type RuleConfidence = 'high' | 'medium' | 'low'
export type SunlightBand = 'full_sun' | 'partial_sun' | 'shade'
export type LawnSizeSource = 'exact' | 'bucket' | 'zone_total' | 'missing'
export type ConfidenceLevel = 'high' | 'medium' | 'low'

// Shared season / grass-class dimensions used across the plan engine.
export type Season = 'spring' | 'summer' | 'fall' | 'winter'
export const SEASONS: readonly Season[] = ['spring', 'summer', 'fall', 'winter']
export type GrassClass = 'cool' | 'warm'
export const GRASS_CLASSES: readonly GrassClass[] = ['cool', 'warm']

export interface RuleSource {
  id: string
  title: string
  institution: string
  url: string
  accessedAt: string
  appliesTo: string[]
}

export interface InputEffect {
  input: 'grass_type' | 'sunlight' | 'lawn_size' | 'zones' | 'weather' | 'history'
  effect: string
  affectedOutputs: string[]
  confidence: RuleConfidence
  sourceIds: string[]
  /**
   * Optional machine-readable discriminator so consumers (e.g. buildPlanConfidence)
   * can react to a specific effect without prose-matching `effect`. Currently only
   * 'grass_shade_mismatch' is acted on (downgrades sunlight/grassType confidence).
   */
  kind?: 'grass_shade_mismatch' | string
}

export interface PlanConfidence {
  grassType: ConfidenceLevel
  lawnSize: ConfidenceLevel
  sunlight: ConfidenceLevel
  zones: ConfidenceLevel
  weather: ConfidenceLevel
  history: ConfidenceLevel
}

export interface GrassTypeResolution {
  raw: string | null
  canonical: string
  isKnown: boolean
  ambiguous: boolean
  alternatives: string[]
  confidence: ConfidenceLevel
  sourceIds: string[]
}

export interface SunlightResolution {
  raw: string | null
  canonical: SunlightBand | null
  confidence: ConfidenceLevel
  sourceIds: string[]
}

export interface LawnSizeResolution {
  raw: string | null
  sqft: number | null
  source: LawnSizeSource
  confidence: ConfidenceLevel
  sourceIds: string[]
}

export interface CanonicalZoneProfile {
  id?: string | null
  name?: string | null
  grassType: string
  grassTypeRaw: string | null
  sunlight: SunlightBand | null
  areaSqft: number | null
  soilType?: string | null
  isEnabled: boolean
}

export interface CanonicalLawnProfile {
  id: string
  grassType: string
  grassTypeRaw: string | null
  grassTypeResolution: GrassTypeResolution
  sunlight: SunlightBand | null
  sunlightRaw: string | null
  sunlightResolution: SunlightResolution
  lawnSizeSqft: number | null
  lawnSizeRaw: string | null
  lawnSizeResolution: LawnSizeResolution
  zones: CanonicalZoneProfile[]
  zoneAreaSqft: number | null
}

export interface DeterministicPlanMetadata {
  engineVersion: string
  deterministic: true
  ruleSources: RuleSource[]
  inputEffects: InputEffect[]
  confidence: PlanConfidence
}

export interface ToolEngineMetadata extends DeterministicPlanMetadata {
  toolId: string
}

export interface DeterministicToolResultMetadata {
  engineVersion?: string
  deterministic?: true
  ruleSources?: RuleSource[]
  inputEffects?: InputEffect[]
  confidence?: PlanConfidence
  toolEngine?: ToolEngineMetadata
}
